<script setup>
import BcsTooltip from '../BcsTooltip.vue'

const props = defineProps({
  // Unique ID for the input
  id: {
    type: String,
    required: true
  },
  // Filter label
  label: {
    type: String,
    required: true
  },
  // Input value
  modelValue: {
    type: [String, Number],
    default: ''
  },
  // Placeholder
  placeholder: {
    type: String,
    default: 'Enter value'
  },
  // Input type
  type: {
    type: String,
    default: 'text'
  },
  // Custom width (optional)
  width: {
    type: String,
    default: 'w-100'
  },
  // Error message (optional)
  errorMessage: {
    type: String,
    default: ''
  },
  // Tooltip text (optional) - shows info icon with tooltip when not empty
  tooltipText: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'blur', 'clear'])

const clearValue = () => {
  emit('update:modelValue', '')
  emit('clear')
}

const handleInput = (event) => {
  emit('update:modelValue', event.target.value)
}

const handleChange = (event) => {
  emit('change', event.target.value)
}
const handleBlur = (event) => {
  emit('blur', event.target.value)
}
</script>

<template>
  <div class="filter-input-wrapper">
    <label :for="id" class="text-14 fw-bold mb-1 d-flex align-items-center gap-1">
      {{ label }}
      <BcsTooltip v-if="tooltipText" :simpleText="tooltipText" :show-arrow="true" :contentClass="'simple-text-tooltip'">
        <i class="bi bi-exclamation-circle text-primary"></i>
      </BcsTooltip>
    </label>
    <div class="input-wrapper" :class="width">
      <input
        :id="id"
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :class="['form-control', errorMessage ? 'is-invalid' : '']"
        class="header-20 py-2"
        @input="handleInput"
        @change="handleChange"
        @blur="handleBlur"
      />
      <button
        v-if="modelValue && !errorMessage"
        type="button"
        class="clear-btn"
        @click="clearValue"
        aria-label="Clear input"
      >
        <i class="bi bi-x-circle-fill"></i>
      </button>
    </div>
    <div v-if="errorMessage" class="invalid-feedback d-block error-overlay">{{ errorMessage }}</div>
  </div>
</template>

<style scoped lang="scss">
.form-control {
  border: 1px solid var(--bs-primary);
  color: var(--bs-primary);
  border-radius: 0.375rem;
  background: #fff;
  font-weight: 500;
  
  &::placeholder {
    color: var(--bs-gray-600);
  }
  
  &:focus {
    border-color: var(--bs-primary);
    box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25);
  }
}

.form-control.is-invalid {
  border-color: #dc3545 !important;
}

.filter-input-wrapper { position: relative; }

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.clear-btn {
  position: absolute;
  right: 0.8rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--bs-primary);
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-overlay {
  position: absolute;
  left: 0;
  top: calc(100% + 2px);
  font-size: 0.65rem;
  pointer-events: none;
  color: #dc3545 !important;
  line-height: 1;
}

</style>
